import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

export interface SearchableOption {
  value: string;
  label: string;
}

/**
 * Searchable single-select — a trigger button that opens a popover with a
 * search box + filtered list. For dropdowns with large option lists (e.g. the
 * UWM company picker with thousands of brokers) a native <select> is unusable.
 * The rendered list is capped (search to narrow) to stay fast on huge lists.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  disabled = false,
}: {
  options: SearchableOption[];
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
    return base.slice(0, 200);
  }, [options, query]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex w-full items-center justify-between rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm transition-colors',
          'hover:border-brand-navy-300 focus:border-brand-navy-500 focus:outline-none focus:ring-2 focus:ring-brand-navy-200',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
          !selected && 'text-slate-400',
        )}
      >
        <span className="truncate">{selected?.label ?? placeholder}</span>
        <svg
          aria-hidden
          viewBox="0 0 10 6"
          className={clsx('ml-2 h-2.5 w-2.5 shrink-0 text-slate-500 transition-transform', open && 'rotate-180')}
        >
          <path d="M0 0 L5 6 L10 0" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-1 max-h-72 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-floating">
          <div className="border-b border-slate-100 p-2">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs shadow-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-200"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1 text-sm">
            {filtered.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">No matches</li>}
            {filtered.map((o) => (
              <li key={o.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQuery('');
                  }}
                  className={clsx(
                    'block w-full px-3 py-1.5 text-left text-slate-800 hover:bg-brand-navy-50',
                    o.value === value && 'bg-brand-navy-50/60 font-medium',
                  )}
                >
                  {o.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
